import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ChartLoaderWrapperComponent } from '@enginuity/analytics/organisms/analytics-loader/analytics-loading-wrapper.component';
import { AnalyticsTrendWidgetComponent } from '@enginuity/analytics/organisms/analytics-trend-widget/analytics-trend-widget.component';
import { CustomBarChartContainerComponent } from '@enginuity/analytics/organisms/custom-bar-chart-container/custom-bar-chart-container.component';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';
import { ListFooterComponent } from '@enginuity/core/organisms/list-footer/list-footer.component';
import { TableViewComponent } from '@enginuity/core/organisms/table-view/table-view.component';
import { AnalyticsRangeContext } from '@services/analytics-services/core';
import { Pagination } from '@services/core-services/models';
import { DashboardorderList } from '@services/order-services/constants/dashboard-order-table-header.constant';
import { mapDashboardOrderTableData } from '@services/order-services/constants/dashboard-order.table.mapper';
import { TableView } from '@services/ui-services/models';
import { Subject, takeUntil, tap, switchMap, forkJoin } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { DashboardOrdersStore } from './dashboard-orders.store';

@Component({
  selector: 'app-dashboard-orders',
  standalone: true,
  imports: [
    AnalyticsTrendWidgetComponent,
    AsyncPipe,
    TableViewComponent,
    ListFooterComponent,
    CustomBarChartContainerComponent,
    ChartLoaderWrapperComponent,
    GlobalLoaderWrapperComponent,
  ],
  templateUrl: './dashboard-orders.component.html',
  styleUrl: './dashboard-orders.component.scss',
  providers: [DashboardOrdersStore],
})
export class DashboardOrdersComponent implements OnInit, OnDestroy {
  // Get signals directly from the store
  protected rangeContext = this.dashboardStore.getRangeContextSignal();
  protected comparePeriod = this.dashboardStore.getComparePeriodSignal();

  // Create resources using the new resource pattern
  protected orderAnalyticsResource = this.dashboardOrdersStore.createOrderAnalyticsResource(
    this.rangeContext
  );

  public orderAnalyticsLoader$ = this.dashboardOrdersStore.getOrdersLoader();
  public associatedCampaignsLoader$ = this.dashboardOrdersStore.getAssociatedCampaignsLoader();
  protected orderLoader$ = this.dashboardOrdersStore.getOrdersLoader();
  protected orderErrorMessage$ = this.dashboardOrdersStore.getErrorMessage();
  protected associatedCampaignsStats$ = this.dashboardOrdersStore.getAssociatedCampaignsStats();
  protected associatedCampaignsTabs$ = this.dashboardOrdersStore.getAssociatedCampaignsTabs();
  protected associatedCampaignsTabsActiveTab$ =
    this.dashboardOrdersStore.getAssociatedCampaignsTabsActiveTab();
  protected associatedCampaignsPagination$ =
    this.dashboardOrdersStore.getAssociatedCampaignsPagination();
  protected ordersList$ = this.dashboardOrdersStore.getOrderList();

  private destroy$ = new Subject<void>();
  protected pagination: Pagination | undefined;
  protected orderTable: TableView = {
    head: DashboardorderList,
    rows: [],
    data: [],
  };
  protected associatedCampaignsTitles: string[] = ['User count'];

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dashboardOrdersStore: DashboardOrdersStore,
    private router: Router
  ) {
    this.ordersList$
      .pipe(
        takeUntil(this.destroy$),
        tap(({ list, pagination }) => {
          this.orderTable.rows = list.map(order => mapDashboardOrderTableData(order));
          this.pagination = pagination;
        })
      )
      .subscribe();
  }

  onActionClick(entry: any) {
    if (entry.action == 'customer-order-detail') {
      this.router.navigateByUrl(`orders/${entry.orderId}/summary`);
    }
  }

  onSort(event: any) {
    if (event && typeof event === 'object') {
      const keys = Object.keys(event);
      if (keys.length === 0) {
        this.dashboardOrdersStore.resetSorting();
        return;
      }
      const field = keys[0];
      const direction = event[field];
      if (field && direction) {
        this.dashboardOrdersStore.sortOrders(field, direction);
      }
    }
  }

  ngOnInit(): void {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((context: AnalyticsRangeContext) => {
          if (context) {
            // Use forkJoin to execute all API calls in parallel
            // switchMap will cancel previous forkJoin if a new context is emitted
            return forkJoin([
              this.dashboardOrdersStore.loadDashboardOrders(context),
              this.dashboardOrdersStore.loadAssociatedCampaignsData(context),
            ]);
          }
          return [];
        })
      )
      .subscribe();
  }

  onPageChange(page: number) {
    this.dashboardOrdersStore.paginateOrders(page);
  }

  protected onAssociatedCampaignsTabChange(tabId: string): void {
    this.dashboardOrdersStore.updateAssociatedCampaignsDefaultTab(tabId);
  }

  protected onAssociatedCampaignsPageChange(event: {
    tabId: string;
    page: number;
    limit: number;
  }): void {
    this.dashboardOrdersStore.updateAssociatedCampaignsPagination(
      event.tabId,
      event.page,
      event.limit
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
